import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import {
    DocumentDto,
    InquiryDto,
    InquiryOrganizationAccessGrantDto,
    InquiryVariantPreviewDto
} from '@travel-planner/library-travel-planner-api';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { InquiriesActions } from './inquiries.actions';
import { LoadingState } from '../../../../core/models/common';

export interface InquiriesState extends EntityState<InquiryDto> {
    loadingState: LoadingState;
    inquiryDocuments: { [id: number]: DocumentDto[] };
    inquiryVariantPreviews: { [id: number]: InquiryVariantPreviewDto[] };
    organizationAccessGrants: { [inquiryId: number]: InquiryOrganizationAccessGrantDto[] };
    organizationAccessGrantLoadingStates: { [inquiryId: number]: LoadingState };
}

export const inquiriesAdapter: EntityAdapter<InquiryDto> = createEntityAdapter<InquiryDto>();

const initialState: InquiriesState = inquiriesAdapter.getInitialState({
    loadingState: 'Initial',
    inquiryDocuments: {},
    inquiryVariantPreviews: {},
    organizationAccessGrants: {},
    organizationAccessGrantLoadingStates: {},
});

export const inquiriesFeature = createFeature({
    name: 'inquiries',
    reducer: createReducer(
        initialState,
        on(InquiriesActions.loadInquiry, (state) => ({
            ...state,
            loadingState: 'Loading' as const
        })),
        on(InquiriesActions.loadInquirySuccess, (state, { inquiry }) => {
            return inquiriesAdapter.upsertOne(inquiry, {
                ...state,
                loadingState: 'Loaded' as const
            });
        }),
        on(InquiriesActions.addInquiry, (state) => ({
            ...state,
            loadingState: 'Loading' as const
        })),
        on(InquiriesActions.addInitialInquiryVariant, (state) => ({
            ...state,
            loadingState: 'Loading' as const
        })),
        on(InquiriesActions.addInquirySuccess, (state, { inquiry }) => {
            return inquiriesAdapter.addOne(inquiry, {
                ...state,
                loadingState: 'Loaded' as const
            });
        }),
        on(InquiriesActions.addInitialInquiryVariantSuccess, (state) => ({
            ...state,
            loadingState: 'Loaded' as const
        })),
        on(InquiriesActions.updateInquiry, (state) => ({
            ...state,
            loadingState: 'Loading' as const
        })),
        on(InquiriesActions.updateInquirySuccess, (state, { inquiry }) => {
            return inquiriesAdapter.updateOne({ id: inquiry.id, changes: inquiry }, {
                ...state,
                loadingState: 'Loaded' as const
            });
        }),
        on(InquiriesActions.deleteInquiry, (state) => ({
            ...state,
            loadingState: 'Loading' as const
        })),
        on(InquiriesActions.deleteInquirySuccess, (state, { id }) => {
            return inquiriesAdapter.removeOne(id, {
                ...state,
                loadingState: 'Loaded' as const
            });
        }),
        on(InquiriesActions.loadInquiryDocumentsSuccess, (state, { id, documents }) => ({
            ...state,
            inquiryDocuments: {
                ...state.inquiryDocuments,
                [id]: documents
            }
        })),
        on(InquiriesActions.loadInquiryVariantsSuccess, (state, { id, variants }) => ({
            ...state,
            inquiryVariantPreviews: {
                ...state.inquiryVariantPreviews,
                [id]: variants
            }
        })),
        on(
            InquiriesActions.loadOrganizationAccessGrants,
            InquiriesActions.grantOrganizationAccess,
            InquiriesActions.revokeOrganizationAccess,
            (state, { inquiryId }) => ({
                ...state,
                organizationAccessGrantLoadingStates: {
                    ...state.organizationAccessGrantLoadingStates,
                    [inquiryId]: 'Loading' as const
                }
            })
        ),
        on(InquiriesActions.loadOrganizationAccessGrantsSuccess, (state, { inquiryId, grants }) => ({
            ...state,
            organizationAccessGrants: {
                ...state.organizationAccessGrants,
                [inquiryId]: grants
            },
            organizationAccessGrantLoadingStates: {
                ...state.organizationAccessGrantLoadingStates,
                [inquiryId]: 'Loaded' as const
            }
        })),
        on(InquiriesActions.grantOrganizationAccessSuccess, (state, { inquiryId, grant }) => ({
            ...state,
            organizationAccessGrants: {
                ...state.organizationAccessGrants,
                [inquiryId]: [
                    ...(state.organizationAccessGrants[inquiryId] ?? [])
                        .filter(existingGrant => existingGrant.organizationId !== grant.organizationId),
                    grant
                ].sort((left, right) => (left.organizationName ?? '').localeCompare(right.organizationName ?? ''))
            },
            organizationAccessGrantLoadingStates: {
                ...state.organizationAccessGrantLoadingStates,
                [inquiryId]: 'Loaded' as const
            }
        })),
        on(InquiriesActions.revokeOrganizationAccessSuccess, (state, { inquiryId, organizationId }) => ({
            ...state,
            organizationAccessGrants: {
                ...state.organizationAccessGrants,
                [inquiryId]: (state.organizationAccessGrants[inquiryId] ?? [])
                    .filter(grant => grant.organizationId !== organizationId)
            },
            organizationAccessGrantLoadingStates: {
                ...state.organizationAccessGrantLoadingStates,
                [inquiryId]: 'Loaded' as const
            }
        })),
        on(
            InquiriesActions.loadOrganizationAccessGrantsFailure,
            InquiriesActions.grantOrganizationAccessFailure,
            InquiriesActions.revokeOrganizationAccessFailure,
            (state, { inquiryId }) => ({
                ...state,
                organizationAccessGrantLoadingStates: {
                    ...state.organizationAccessGrantLoadingStates,
                    [inquiryId]: 'Error' as const
                }
            })
        ),
        on(
            InquiriesActions.loadInquiryFailure,
            InquiriesActions.addInquiryFailure,
            InquiriesActions.addInitialInquiryVariantFailure,
            InquiriesActions.updateInquiryFailure,
            InquiriesActions.deleteInquiryFailure,
            InquiriesActions.loadInquiryDocumentsFailure,
            InquiriesActions.loadInquiryVariantsFailure,
            (state) => ({
                ...state,
                loadingState: 'Error' as const
            })
        )
    )
});

export const {
    name,
    reducer,
    selectInquiriesState,
} = inquiriesFeature;

const selectSafeInquiriesState = createSelector(
    selectInquiriesState,
    (state) => state ?? initialState
);

export const {
    selectIds: selectInquiryIds,
    selectEntities: selectInquiryEntities,
    selectAll: selectAllInquiries,
    selectTotal: selectInquiriesTotal,
} = inquiriesAdapter.getSelectors(selectSafeInquiriesState);

export const selectInquiry = (id: number) => createSelector(
    selectInquiryEntities,
    (entities) => entities[id] ?? null
);

export const selectInquiryDocuments = (id: number) => createSelector(
    selectSafeInquiriesState,
    (state) => state.inquiryDocuments[id] ?? []
);

export const selectInquiryVariantPreviewss = (id: number) => createSelector(
    selectSafeInquiriesState,
    (state) => state.inquiryVariantPreviews[id] ?? []
);

export const selectInquiriesLoadingState = createSelector(
    selectSafeInquiriesState,
    (state) => state.loadingState
);

export const selectInquiryOrganizationAccessGrants = (inquiryId: number) => createSelector(
    selectSafeInquiriesState,
    state => state.organizationAccessGrants[inquiryId] ?? []
);

export const selectInquiryOrganizationAccessGrantLoadingState = (inquiryId: number) => createSelector(
    selectSafeInquiriesState,
    state => state.organizationAccessGrantLoadingStates[inquiryId] ?? 'Initial'
);
